// P4-SCHED — deterministic duration + CPM engine (pure functions, no React,
// no DB). docs/implementation/SCHEDULE_INTELLIGENCE_MODULE.md §8, §11–§13,
// §35, §44, §59–§60; docs/implementation/P4_SCHEDULE_DURATION.md.
//
// Work pattern is a first-class input (product owner, 2026-09-13: "adjust
// for overtime, 5-8s, 6-10s, double shifts, parallel shifts"):
//   productive crew-hours per work day
//     = shifts/day × (hours/shift + overtime hours/shift × overtime efficiency)
//       with every shift beyond the first × second-shift efficiency
//   crew-hours needed = quantity ÷ rate (units per crew-HOUR — the standard is
//     stored per crew-hour precisely so a 10-hour shift moves more than an
//     8-hour one, honestly)
//   crew-days = crew-hours ÷ productive hours per day
//   raw duration (working days) = crew-days ÷ parallel crews
//     (parallel crews = crews working separate areas / floors at once)
//   adjusted = raw × congestion × occupied-facility × complexity
//   calculated = MAX(minimum, CEILING(adjusted))
// Efficiencies and factors default to 1.00 and are EXPLICIT — nothing about
// overtime fatigue or second-shift productivity is assumed; the product owner
// sets them. Working days → calendar weeks via work days per week; → dates by
// skipping the non-work days of that pattern (5-day: Sat/Sun off; 6-day:
// Sunday off; 7-day: none).
//
// CPM: forward / backward pass over FS / SS / FF / SF relationships with lag,
// early / late start / finish, total and free float, critical flag, the
// critical path, cycle detection. Project duration is an OUTPUT of the
// network (§13) — never an input the activities are squeezed into.
//
// Calculated → Override → Effective (§44): an override never overwrites the
// calculation; both are kept and the difference is information.

export const SHIFT_PATTERNS = [
  { key: "5x8",  label: "5 × 8s",  workDaysPerWeek: 5, hoursPerShift: 8,  shiftsPerDay: 1 },
  { key: "5x10", label: "5 × 10s", workDaysPerWeek: 5, hoursPerShift: 10, shiftsPerDay: 1 },
  { key: "6x10", label: "6 × 10s", workDaysPerWeek: 6, hoursPerShift: 10, shiftsPerDay: 1 },
  { key: "6x12", label: "6 × 12s", workDaysPerWeek: 6, hoursPerShift: 12, shiftsPerDay: 1 },
  { key: "7x12", label: "7 × 12s", workDaysPerWeek: 7, hoursPerShift: 12, shiftsPerDay: 1 },
  { key: "5x8x2", label: "5 × 8s, double shift", workDaysPerWeek: 5, hoursPerShift: 8, shiftsPerDay: 2 },
  { key: "6x10x2", label: "6 × 10s, double shift", workDaysPerWeek: 6, hoursPerShift: 10, shiftsPerDay: 2 },
];

export const DEFAULT_CALENDAR = Object.freeze({
  workDaysPerWeek: 5,
  hoursPerShift: 8,
  shiftsPerDay: 1,
  overtimeHoursPerShift: 0,
  overtimeEfficiency: 1.0,     // productivity of an overtime hour relative to a straight-time hour
  secondShiftEfficiency: 1.0,  // productivity of a second / third shift relative to the first
  defaultCrewCount: 1,
  congestionFactor: 1.0,
  occupiedFacilityFactor: 1.0,
  complexityFactor: 1.0,
  criticalFloatThresholdDays: 0,
});

const num = (v, d = null) => (v == null || v === "" || !Number.isFinite(+v) ? d : +v);

// Header row (ExecutionSchedules, db/78) → calendar object; missing → defaults.
export function calendarFromHeader(h = {}) {
  return {
    workDaysPerWeek:            num(h.WorkDaysPerWeek ?? h.workDaysPerWeek, DEFAULT_CALENDAR.workDaysPerWeek),
    hoursPerShift:              num(h.HoursPerShift ?? h.hoursPerShift, DEFAULT_CALENDAR.hoursPerShift),
    shiftsPerDay:               num(h.ShiftsPerDay ?? h.shiftsPerDay, DEFAULT_CALENDAR.shiftsPerDay),
    overtimeHoursPerShift:      num(h.OvertimeHoursPerShift ?? h.overtimeHoursPerShift, DEFAULT_CALENDAR.overtimeHoursPerShift),
    overtimeEfficiency:         num(h.OvertimeEfficiency ?? h.overtimeEfficiency, DEFAULT_CALENDAR.overtimeEfficiency),
    secondShiftEfficiency:      num(h.SecondShiftEfficiency ?? h.secondShiftEfficiency, DEFAULT_CALENDAR.secondShiftEfficiency),
    defaultCrewCount:           num(h.DefaultCrewCount ?? h.defaultCrewCount, DEFAULT_CALENDAR.defaultCrewCount),
    congestionFactor:           num(h.CongestionFactor ?? h.congestionFactor, DEFAULT_CALENDAR.congestionFactor),
    occupiedFacilityFactor:     num(h.OccupiedFacilityFactor ?? h.occupiedFacilityFactor, DEFAULT_CALENDAR.occupiedFacilityFactor),
    complexityFactor:           num(h.ComplexityFactor ?? h.complexityFactor, DEFAULT_CALENDAR.complexityFactor),
    criticalFloatThresholdDays: num(h.CriticalFloatThresholdDays ?? h.criticalFloatThresholdDays, DEFAULT_CALENDAR.criticalFloatThresholdDays),
    startDate:                  h.StartDate ?? h.startDate ?? null,
  };
}

export function matchShiftPattern(cal) {
  return SHIFT_PATTERNS.find(p => p.workDaysPerWeek === cal.workDaysPerWeek && p.hoursPerShift === cal.hoursPerShift && p.shiftsPerDay === cal.shiftsPerDay)?.key || "custom";
}

// Productive crew-hours per work day for an activity — the activity may
// override shifts / hours / overtime (ScheduleActivities.ShiftsPerDay …),
// else the schedule's calendar applies.
export function productiveHoursPerDay(cal, activity = {}) {
  const shifts = num(activity.ShiftsPerDay ?? activity.shiftsPerDay, cal.shiftsPerDay) || 1;
  const hours = num(activity.HoursPerShift ?? activity.hoursPerShift, cal.hoursPerShift) || 0;
  const ot = num(activity.OvertimeHoursPerShift ?? activity.overtimeHoursPerShift, cal.overtimeHoursPerShift) || 0;
  const perShift = hours + ot * cal.overtimeEfficiency;
  const productive = perShift + (shifts - 1) * perShift * cal.secondShiftEfficiency;
  const paid = shifts * (hours + ot);
  const parts = [`${shifts} shift${shifts === 1 ? "" : "s"} × ${hours} h`];
  if (ot > 0) parts.push(`+ ${ot} h OT${cal.overtimeEfficiency !== 1 ? ` @ ${Math.round(cal.overtimeEfficiency * 100)}%` : ""}`);
  if (shifts > 1 && cal.secondShiftEfficiency !== 1) parts.push(`2nd+ shift @ ${Math.round(cal.secondShiftEfficiency * 100)}%`);
  return { shifts, hours, overtimeHours: ot, paidHours: paid, productiveHours: productive, basis: parts.join(" ") };
}

// One activity's duration from its inputs. Returns every intermediate so the
// UI can answer "why is this 11 days?" (§35).
//   kind: 'quantity' | 'fixed' | 'milestone'
//   quantity + ratePerCrewHour for 'quantity'; fixedDurationDays for 'fixed'
//   crewCount = parallel crews; minimumDurationDays; factors from cal
export function calculateActivityDuration({ kind, quantity, ratePerCrewHour, fixedDurationDays, leadTimeWeeks = null, crewCount, minimumDurationDays, calendar, activity = {}, factors = null }) {
  const cal = calendar || DEFAULT_CALENDAR;
  const minDays = Math.max(0, num(minimumDurationDays, 0));
  const steps = [];
  const warnings = [];
  if (kind === "milestone") return { method: "milestone", calculatedDurationDays: 0, steps: ["Milestone — zero duration"], warnings };

  const f = factors || { congestion: cal.congestionFactor, occupied: cal.occupiedFacilityFactor, complexity: cal.complexityFactor };
  const adjustmentFactor = (num(f.congestion, 1)) * (num(f.occupied, 1)) * (num(f.complexity, 1));

  // Release 2-A — a procurement lead time: calendar WEEKS the vendor quoted /
  // the estimator assumed, expressed in the pattern's working days (a 12-week
  // lead time is 12 × 5 = 60 working days on a 5-day pattern; the calendar
  // then places it on real dates). Congestion / occupied / complexity factors
  // never apply — the factory does not care about our site.
  if (kind === "lead_time") {
    const wk = num(leadTimeWeeks);
    if (wk == null || wk < 0) {
      warnings.push({ code: "lead_time_required", message: "No lead time on the equipment record — enter it (Assumed Equipment ▸ Lead time) so fabrication can be scheduled." });
      return { method: "required", calculatedDurationDays: null, adjustmentFactor: 1, steps: ["Lead time required — none on the equipment record"], warnings };
    }
    const days = wk * (cal.workDaysPerWeek || 5);
    const calc = Math.max(minDays, Math.ceil(days - 1e-9));
    steps.push(`Lead time ${fmt(wk)} week${wk === 1 ? "" : "s"} × ${cal.workDaysPerWeek || 5} work days / week = ${fmt(days)} working days`);
    if (minDays > days) steps.push(`Minimum ${minDays} day${minDays === 1 ? "" : "s"} governs`);
    return { method: "lead_time", leadTimeWeeks: wk, adjustmentFactor: 1, calculatedDurationDays: calc, steps, warnings };
  }

  if (kind === "fixed") {
    const fd = num(fixedDurationDays);
    if (fd == null) {
      warnings.push({ code: "duration_required", message: "No production standard gives this activity a fixed duration — enter one (Setup ▸ Estimate Engine ▸ Production Standards) or override it here." });
      return { method: "required", calculatedDurationDays: null, adjustmentFactor, steps: ["Duration required — no fixed-duration standard"], warnings };
    }
    const adjusted = fd * adjustmentFactor;
    const calc = Math.max(minDays, Math.ceil(adjusted - 1e-9));
    steps.push(`Fixed duration from standard: ${fd} working day${fd === 1 ? "" : "s"}`);
    if (adjustmentFactor !== 1) steps.push(`× factors ${adjustmentFactor.toFixed(3)} = ${adjusted.toFixed(2)}`);
    if (minDays > calc - 1e-9 && minDays > adjusted) steps.push(`Minimum ${minDays} day${minDays === 1 ? "" : "s"} governs`);
    return { method: "fixed", fixedDurationDays: fd, adjustmentFactor, adjustedDurationDays: adjusted, calculatedDurationDays: calc, steps, warnings };
  }

  // quantity
  const q = num(quantity);
  const rate = num(ratePerCrewHour);
  const crews = Math.max(1, num(crewCount, cal.defaultCrewCount) || 1);
  const ph = productiveHoursPerDay(cal, activity);
  if (q == null || q < 0) {
    warnings.push({ code: "quantity_missing", message: "No quantity resolved for this activity." });
    return { method: "required", calculatedDurationDays: null, adjustmentFactor, crewCount: crews, productive: ph, steps: ["Quantity missing"], warnings };
  }
  if (rate == null || rate <= 0) {
    warnings.push({ code: "duration_required", message: "No production standard (units per crew-hour) exists for this activity yet — enter one under Setup ▸ Estimate Engine ▸ Production Standards, or override the duration here." });
    return { method: "required", quantity: q, calculatedDurationDays: null, adjustmentFactor, crewCount: crews, productive: ph, steps: [`Quantity ${q}`, "Duration required — no production standard"], warnings };
  }
  if (!(ph.productiveHours > 0)) {
    warnings.push({ code: "calendar_invalid", message: "The work pattern yields zero productive hours per day." });
    return { method: "required", quantity: q, calculatedDurationDays: null, adjustmentFactor, crewCount: crews, productive: ph, steps: ["Zero productive hours per day"], warnings };
  }
  const crewHours = q / rate;
  const crewDays = crewHours / ph.productiveHours;
  const rawDays = crewDays / crews;
  const adjusted = rawDays * adjustmentFactor;
  const ceil = Math.ceil(adjusted - 1e-9);
  const calc = q === 0 ? 0 : Math.max(minDays, ceil);
  steps.push(`${fmt(q)} ÷ ${fmt(rate)} per crew-hour = ${fmt(crewHours)} crew-hours`);
  steps.push(`÷ ${fmt(ph.productiveHours)} productive h/day (${ph.basis}) = ${fmt(crewDays)} crew-days`);
  steps.push(`÷ ${crews} parallel crew${crews === 1 ? "" : "s"} = ${fmt(rawDays)} working days`);
  if (adjustmentFactor !== 1) steps.push(`× factors ${adjustmentFactor.toFixed(3)} = ${fmt(adjusted)}`);
  steps.push(`Rounded up: ${ceil} day${ceil === 1 ? "" : "s"}`);
  if (q > 0 && minDays > ceil) steps.push(`Minimum ${minDays} day${minDays === 1 ? "" : "s"} governs`);
  return { method: "quantity", quantity: q, ratePerCrewHour: rate, crewCount: crews, productive: ph, crewHours, crewDays, rawDurationDays: rawDays, adjustmentFactor, adjustedDurationDays: adjusted, minimumDurationDays: minDays, calculatedDurationDays: calc, steps, warnings };
}

const fmt = (v) => Number.isFinite(v) ? (Math.round(v * 100) / 100).toLocaleString() : "—";

// Calculated → Override → Effective. `required` = nothing to schedule with yet.
export function resolveEffectiveDuration(activityRow, calc) {
  const calculated = calc?.calculatedDurationDays ?? null;
  const override = num(activityRow?.OverrideDurationDays ?? activityRow?.overrideDurationDays);
  const effective = override ?? calculated;
  return { calculated, override, effective, isOverridden: override != null, required: effective == null };
}

// Evaluate a whole schedule: per-activity calculation + effective durations,
// then CPM over the effective network. Activities with no duration yet are
// scheduled at 0 days and flagged, so the network still resolves and the
// missing pieces are visible rather than hidden.
//   header        ExecutionSchedules row (or calendar-shaped object)
//   activities    ScheduleActivities rows
//   dependencies  ScheduleDependencies rows
//   standardsByType  { [ActivityType]: standard row } (current versions) — used
//                 only for an activity that has no snapshot of its own
export function evaluateSchedule({ header, activities = [], dependencies = [], standardsByType = {} }) {
  const cal = calendarFromHeader(header || {});
  const evaluated = activities.map(a => {
    const std = standardsByType[a.ActivityType] || null;
    // Release 2-A: a fabrication activity stores its lead time as the source quantity (LEAD_TIME_WEEKS).
    const kind = a.IsMilestone ? "milestone" : a.SourceQuantityType === "LEAD_TIME_WEEKS" ? "lead_time" : (a.SourceQuantityType ? "quantity" : "fixed");
    const rate = a.RatePerCrewHour ?? std?.RatePerCrewHour ?? null;
    const fixed = a.FixedDurationDays ?? std?.FixedDurationDays ?? null;
    const minDays = a.MinimumDurationDays ?? std?.MinimumDurationDays ?? 0;
    const calc = calculateActivityDuration({
      kind, quantity: a.SourceQuantity, ratePerCrewHour: rate, fixedDurationDays: fixed, leadTimeWeeks: kind === "lead_time" ? a.SourceQuantity : null,
      crewCount: a.CrewCount ?? cal.defaultCrewCount, minimumDurationDays: minDays, calendar: cal, activity: a,
      ...(kind === "lead_time" ? { factors: { congestion: 1, occupied: 1, complexity: 1 } } : {}),
    });
    const eff = resolveEffectiveDuration(a, calc);
    return { ...a, calc, eff, kind, standardUsed: a.RatePerCrewHour != null || a.FixedDurationDays != null ? "snapshot" : (std ? "current" : null) };
  });
  const net = cpm(
    evaluated.map(a => ({ id: a.ActivityID ?? a.ActivityKey, duration: a.eff.effective ?? 0 })),
    dependencies.map(d => ({ predId: d.PredecessorActivityID ?? d.predecessorId, succId: d.SuccessorActivityID ?? d.successorId, type: d.RelationshipType || "FS", lag: num(d.LagDays, 0) })),
    { criticalFloatThreshold: cal.criticalFloatThresholdDays },
  );
  const rows = evaluated.map(a => ({ ...a, cpm: net.byId[a.ActivityID ?? a.ActivityKey] || null }));
  const requiredCount = rows.filter(r => r.eff.required).length;
  const summary = {
    projectDurationDays: net.projectDurationDays,
    projectDurationWeeks: net.projectDurationDays / (cal.workDaysPerWeek || 5),
    criticalCount: rows.filter(r => r.cpm?.critical && !r.IsMilestone).length,
    requiredCount,
    overriddenCount: rows.filter(r => r.eff.isOverridden).length,
    cycles: net.cycles,
    complete: requiredCount === 0 && !net.cycles,
    finishDate: cal.startDate && !net.cycles ? workingDayToDate(cal.startDate, net.projectDurationDays, cal.workDaysPerWeek) : null,
  };
  return { calendar: cal, activities: rows, network: net, summary };
}

// ── CPM ──────────────────────────────────────────────────────────────────────
// activities [{ id, duration }] (working days, ≥ 0), dependencies [{ predId,
// succId, type FS|SS|FF|SF, lag }]. Day 0 = project start. Returns
// { byId: { ES, EF, LS, LF, totalFloat, freeFloat, critical }, projectDurationDays,
//   criticalPath: [ids in order], cycles, order }.
export function cpm(activities, dependencies, { criticalFloatThreshold = 0 } = {}) {
  const ids = activities.map(a => a.id);
  const dur = Object.fromEntries(activities.map(a => [a.id, Math.max(0, num(a.duration, 0))]));
  const idSet = new Set(ids);
  const deps = dependencies.filter(d => idSet.has(d.predId) && idSet.has(d.succId) && d.predId !== d.succId);
  const succs = {}, preds = {};
  for (const id of ids) { succs[id] = []; preds[id] = []; }
  for (const d of deps) { succs[d.predId].push(d); preds[d.succId].push(d); }

  // Kahn topological order — a cycle leaves nodes unordered.
  const indeg = Object.fromEntries(ids.map(id => [id, preds[id].length]));
  const queue = ids.filter(id => indeg[id] === 0);
  const order = [];
  while (queue.length) {
    const id = queue.shift();
    order.push(id);
    for (const d of succs[id]) if (--indeg[d.succId] === 0) queue.push(d.succId);
  }
  const cycles = order.length !== ids.length;
  const byId = {};
  if (cycles) {
    for (const id of ids) byId[id] = { ES: null, EF: null, LS: null, LF: null, totalFloat: null, freeFloat: null, critical: false };
    return { byId, projectDurationDays: null, criticalPath: [], cycles: true, order };
  }

  // Forward pass
  const ES = {}, EF = {};
  for (const id of order) {
    let es = 0;
    for (const d of preds[id]) {
      const lag = num(d.lag, 0);
      const c = d.type === "SS" ? ES[d.predId] + lag
        : d.type === "FF" ? EF[d.predId] + lag - dur[id]
        : d.type === "SF" ? ES[d.predId] + lag - dur[id]
        : EF[d.predId] + lag; // FS
      if (c > es) es = c;
    }
    ES[id] = es;
    EF[id] = es + dur[id];
  }
  const projectDurationDays = ids.length ? Math.max(...ids.map(id => EF[id])) : 0;

  // Backward pass
  const LS = {}, LF = {};
  for (const id of [...order].reverse()) {
    let lf = projectDurationDays;
    for (const d of succs[id]) {
      const lag = num(d.lag, 0);
      const s = d.succId;
      const c = d.type === "SS" ? LS[s] - lag + dur[id]
        : d.type === "FF" ? LF[s] - lag
        : d.type === "SF" ? LF[s] - lag + dur[id]
        : LS[s] - lag; // FS
      if (c < lf) lf = c;
    }
    LF[id] = lf;
    LS[id] = lf - dur[id];
  }

  for (const id of ids) {
    const totalFloat = LS[id] - ES[id];
    let freeFloat = null;
    for (const d of succs[id]) {
      const lag = num(d.lag, 0);
      const s = d.succId;
      const slack = d.type === "SS" ? ES[s] - lag - ES[id]
        : d.type === "FF" ? EF[s] - lag - EF[id]
        : d.type === "SF" ? EF[s] - lag - ES[id]
        : ES[s] - lag - EF[id];
      freeFloat = freeFloat == null ? slack : Math.min(freeFloat, slack);
    }
    if (freeFloat == null) freeFloat = projectDurationDays - EF[id];
    byId[id] = { ES: ES[id], EF: EF[id], LS: LS[id], LF: LF[id], totalFloat, freeFloat, critical: totalFloat <= criticalFloatThreshold + 1e-9 };
  }

  // Critical path: walk critical activities in topological order following
  // critical dependencies from a critical start.
  const criticalPath = order.filter(id => byId[id].critical);
  return { byId, projectDurationDays, criticalPath, cycles: false, order };
}

// ── Calendar arithmetic ───────────────────────────────────────────────────────
// Work days per week decide which weekdays are off: 5 → Sat + Sun, 6 → Sun,
// 7 → none, 4 → Fri + Sat + Sun. dayIndex 0 = the start date itself (moved
// forward to the first work day).
export function isWorkDay(date, workDaysPerWeek = 5) {
  const dow = date.getUTCDay(); // 0 Sun … 6 Sat
  const off = workDaysPerWeek >= 7 ? [] : workDaysPerWeek === 6 ? [0] : workDaysPerWeek === 5 ? [0, 6] : workDaysPerWeek === 4 ? [0, 5, 6] : [0, 6];
  return !off.includes(dow);
}
export function workingDayToDate(startDate, dayIndex, workDaysPerWeek = 5) {
  if (!startDate) return null;
  const d = new Date(typeof startDate === "string" ? `${startDate.slice(0, 10)}T00:00:00Z` : startDate);
  if (Number.isNaN(d.getTime())) return null;
  let remaining = Math.max(0, Math.round(dayIndex));
  while (!isWorkDay(d, workDaysPerWeek)) d.setUTCDate(d.getUTCDate() + 1);
  while (remaining > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (isWorkDay(d, workDaysPerWeek)) remaining -= 1;
  }
  return d.toISOString().slice(0, 10);
}
export const workingDaysToWeeks = (days, workDaysPerWeek = 5) => (Number.isFinite(days) ? days / (workDaysPerWeek || 5) : null);

// Stable fingerprint of the quantities a schedule was generated from — a
// changed estimate marks its schedule stale instead of quietly keeping an
// obsolete one (§38). djb2 over a canonical string; not cryptographic.
export function scheduleFingerprint(activities = []) {
  const s = activities
    .filter(a => (a.origin ?? a.Origin) !== "manual")
    .map(a => `${a.activityKey ?? a.ActivityKey}:${a.sourceQuantityType ?? a.SourceQuantityType ?? ""}:${Math.round(((a.sourceQuantity ?? a.SourceQuantity) || 0) * 100) / 100}`)
    .sort().join("|");
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return `djb2:${(h >>> 0).toString(16)}:${activities.length}`;
}
